import { useEffect, useState, useCallback } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { supabase } from '@/lib/supabase';
import { banners } from '@/mocks/admin';

interface NewsItem {
  id: string;
  title: string;
  content: string | null;
  category: string | null;
  image_url: string | null;
  is_published: boolean;
  published_at: string | null;
  created_at: string;
}

const emptyForm = {
  title: '',
  content: '',
  category: 'Thị trường',
  image_url: '',
  is_published: true,
};

export default function AdminBanners() {
  const [newsList, setNewsList] = useState<NewsItem[]>([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [newsError, setNewsError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [formSaving, setFormSaving] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [genMsg, setGenMsg] = useState('');

  const fetchNews = useCallback(async () => {
    try {
      setNewsLoading(true);
      setNewsError('');
      const { data, error } = await supabase
        .from('news')
        .select('id, title, content, category, image_url, is_published, published_at, created_at')
        .order('published_at', { ascending: false });
      if (error) throw error;
      setNewsList((data as NewsItem[]) || []);
    } catch (e: any) {
      setNewsError(e.message || 'Không thể tải tin tức');
    } finally {
      setNewsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (item: NewsItem) => {
    setEditingId(item.id);
    setForm({
      title: item.title || '',
      content: item.content || '',
      category: item.category || 'Thị trường',
      image_url: item.image_url || '',
      is_published: item.is_published ?? true,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleSave = async () => {
    if (!form.title.trim()) return;
    try {
      setFormSaving(true);
      if (editingId) {
        const { error } = await supabase
          .from('news')
          .update({
            title: form.title.trim(),
            content: form.content.trim(),
            category: form.category.trim(),
            image_url: form.image_url.trim() || null,
            is_published: form.is_published,
          })
          .eq('id', editingId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('news').insert({
          title: form.title.trim(),
          content: form.content.trim(),
          category: form.category.trim(),
          image_url: form.image_url.trim() || null,
          is_published: form.is_published,
          published_at: form.is_published ? new Date().toISOString() : null,
        });
        if (error) throw error;
      }
      closeModal();
      await fetchNews();
    } catch (e: any) {
      setNewsError(e.message || 'Lỗi khi lưu tin tức');
    } finally {
      setFormSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Xác nhận xóa tin tức này?')) return;
    try {
      setNewsLoading(true);
      const { error } = await supabase.from('news').delete().eq('id', id);
      if (error) throw error;
      await fetchNews();
    } catch (e: any) {
      setNewsError(e.message || 'Lỗi khi xóa');
      setNewsLoading(false);
    }
  };

  const handleGenerate = async () => {
    try {
      setGenLoading(true);
      setGenMsg('');
      const { data } = await supabase.functions.invoke('generate-daily-news');
      setGenMsg(data?.success ? 'Đã tạo tin tức hàng ngày thành công!' : data?.message || 'Hoàn tất');
      await fetchNews();
    } catch (e: any) {
      setGenMsg(e.message || 'Lỗi khi tạo tin tức');
    } finally {
      setGenLoading(false);
    }
  };

  function formatDate(iso: string | null) {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Banner section giữ nguyên mock */}
        <div>
          <div className="flex items-center justify-between mb-5">
            <h1 className="text-2xl font-bold text-foreground-950">Quản lý banner</h1>
            <button className="px-4 py-2 rounded-md bg-accent-500 text-background-950 text-sm font-semibold hover:bg-accent-600 whitespace-nowrap cursor-pointer transition-colors">
              <i className="ri-add-line mr-1"></i> Thêm banner
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {banners.map((b) => (
              <div key={b.id} className="rounded-xl border border-background-200/60 bg-background-100 overflow-hidden">
                <div className="h-36 w-full">
                  <img src={b.image} alt={b.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-foreground-950">{b.title}</p>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${b.active ? 'bg-emerald-500/15 text-emerald-400' : 'bg-foreground-500/15 text-foreground-600'}`}>
                      {b.active ? 'Đang hiển thị' : 'Ẩn'}
                    </span>
                  </div>
                  <p className="text-xs text-foreground-600 mt-1">Cập nhật: {b.updated}</p>
                  <div className="flex gap-2 mt-3">
                    <button className="px-3 py-1.5 rounded-md border border-background-300/60 text-xs font-semibold text-foreground-700 hover:border-accent-500 hover:text-accent-400 whitespace-nowrap cursor-pointer transition-colors">Chỉnh sửa</button>
                    <button className="px-3 py-1.5 rounded-md border border-background-300/60 text-xs font-semibold text-foreground-700 hover:border-rose-400 hover:text-rose-400 whitespace-nowrap cursor-pointer transition-colors">Xóa</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* News section — thật từ DB */}
        <div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
            <h2 className="text-xl font-bold text-foreground-950">Quản lý tin tức</h2>
            <div className="flex items-center gap-2">
              {genMsg && (
                <span className={`text-xs font-medium ${genMsg.includes('thành công') ? 'text-emerald-500' : 'text-accent-500'}`}>{genMsg}</span>
              )}
              <button
                onClick={handleGenerate}
                disabled={genLoading}
                className="px-3 py-2 rounded-md bg-secondary-500 text-background-950 text-sm font-semibold hover:bg-secondary-600 whitespace-nowrap cursor-pointer transition-colors disabled:opacity-50"
              >
                <i className={`ri-magic-line mr-1 ${genLoading ? 'animate-spin' : ''}`}></i>
                {genLoading ? 'Đang tạo...' : 'Tạo tin tự động'}
              </button>
              <button
                onClick={openCreate}
                className="px-4 py-2 rounded-md bg-accent-500 text-background-950 text-sm font-semibold hover:bg-accent-600 whitespace-nowrap cursor-pointer transition-colors"
              >
                <i className="ri-add-line mr-1"></i> Thêm tin tức
              </button>
            </div>
          </div>

          {newsError && (
            <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-600">
              {newsError}
              <button onClick={() => setNewsError('')} className="ml-2 underline cursor-pointer">Đóng</button>
            </div>
          )}

          {newsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : newsList.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 rounded-xl border border-background-200/60 bg-background-100">
              <div className="w-14 h-14 rounded-full bg-background-200 flex items-center justify-center mb-3">
                <i className="ri-newspaper-line text-2xl text-foreground-500"></i>
              </div>
              <p className="text-sm font-medium text-foreground-700">Chưa có tin tức</p>
              <p className="text-xs text-foreground-500 mt-1">Nhấn "Tạo tin tự động" hoặc "Thêm tin tức" để bắt đầu</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-background-200/60">
              <table className="w-full text-sm min-w-[640px]">
                <thead>
                  <tr className="bg-background-100 text-left">
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600">Tiêu đề</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600">Danh mục</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600">Ngày đăng</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600">Trạng thái</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600 text-right">Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {newsList.map((n) => (
                    <tr key={n.id} className="border-t border-background-200/40 hover:bg-background-100/60 transition-colors">
                      <td className="px-4 py-3 text-foreground-950 font-medium max-w-xs truncate">{n.title}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 rounded-full bg-accent-500/15 text-accent-400 text-xs font-semibold whitespace-nowrap">{n.category || 'Tin tức'}</span>
                      </td>
                      <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{formatDate(n.published_at)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${n.is_published ? 'bg-emerald-500/15 text-emerald-400' : 'bg-foreground-500/15 text-foreground-600'}`}>
                          {n.is_published ? 'Đã đăng' : 'Nháp'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button onClick={() => openEdit(n)} className="px-3 py-1.5 rounded-md border border-background-300/60 text-xs font-semibold text-foreground-700 hover:border-accent-500 hover:text-accent-400 cursor-pointer transition-colors mr-1">Sửa</button>
                        <button onClick={() => handleDelete(n.id)} className="px-3 py-1.5 rounded-md border border-background-300/60 text-xs font-semibold text-foreground-700 hover:border-rose-400 hover:text-rose-400 cursor-pointer transition-colors">Xóa</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal thêm/sửa tin tức */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-lg rounded-xl border border-background-200/60 bg-background-100 p-5 md:p-6 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-foreground-950">{editingId ? 'Chỉnh sửa tin tức' : 'Thêm tin tức mới'}</h3>
              <button onClick={closeModal} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-200 text-foreground-500 cursor-pointer">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1">Tiêu đề</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full rounded-md border border-background-300/60 bg-background-50 px-3 py-2 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  placeholder="Nhập tiêu đề tin tức..."
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1">Danh mục</label>
                <input
                  type="text"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full rounded-md border border-background-300/60 bg-background-50 px-3 py-2 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1">Tóm tắt</label>
                <textarea
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  rows={4}
                  maxLength={500}
                  className="w-full rounded-md border border-background-300/60 bg-background-50 px-3 py-2 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
                  placeholder="Nội dung tóm tắt tin tức..."
                />
                <p className="text-xs text-foreground-500 mt-0.5 text-right">{form.content.length}/500</p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1">URL hình ảnh</label>
                <input
                  type="text"
                  value={form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  className="w-full rounded-md border border-background-300/60 bg-background-50 px-3 py-2 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  placeholder="https://..."
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  id="published"
                  type="checkbox"
                  checked={form.is_published}
                  onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
                  className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400 cursor-pointer"
                />
                <label htmlFor="published" className="text-sm text-foreground-700 cursor-pointer">Xuất bản ngay</label>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 mt-5">
              <button onClick={closeModal} className="px-4 py-2 rounded-md border border-background-300 text-sm font-semibold text-foreground-700 hover:bg-background-200 whitespace-nowrap cursor-pointer transition-colors">
                Hủy
              </button>
              <button
                onClick={handleSave}
                disabled={!form.title.trim() || formSaving}
                className="px-4 py-2 rounded-md bg-primary-500 text-background-950 text-sm font-semibold hover:bg-primary-600 whitespace-nowrap cursor-pointer transition-colors disabled:opacity-50"
              >
                {formSaving ? 'Đang lưu...' : editingId ? 'Cập nhật' : 'Tạo tin'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}